import { Pic } from "./Pic";

import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Upload } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";
import { PROJECTS, QuoteSimilarButton } from "./Projects";

const projectOf = (id) => PROJECTS.find((p) => p.id === id);
const titleOf = (id) => projectOf(id)?.title ?? "";

const BEAMS = [
  { id: "doppias", shape: "Curva a doppia S", project: "p16", img: "/img/emma/prj-cantina-0.jpg", path: "M2 30 C 20 2, 40 2, 50 20 S 80 38, 98 10", wide: true },
  { id: "onda", shape: "Travi a onda", project: "p21", img: "/img/emma/prj-auditorium-0.jpg", path: "M2 20 Q 14 4 26 20 T 50 20 T 74 20 T 98 20" },
  { id: "esse", shape: "Curva a S", project: "p14", img: "/img/emma/prj-piscina-0.jpg", path: "M2 34 C 45 34, 55 6, 98 6" },
  { id: "nave", shape: "Nave capovolta", project: "p17", img: "/img/emma/prj-museo-0.jpg", path: "M6 6 Q 50 52 94 6", wide: true },
  { id: "centinata", shape: "Trave centinata", project: "p15", img: "/img/emma/prj-salesiano-0.jpg", path: "M2 34 Q 50 -4 98 34" },
  { id: "cupola", shape: "Cupola in microlamellare", project: "p25", img: "/img/emma/prj-cupola-chiesa-0.jpg", path: "M2 36 Q 50 -28 98 36 M50 4 L50 36" },
  { id: "lucernario", shape: "Curve sagomate su lucernario", project: "p27", img: "/img/emma/prj-polivalente2-0.jpg", path: "M2 30 Q 26 6 50 30 Q 74 6 98 30" },
];

const Profile = ({ d }) => (
  <svg viewBox="0 0 100 40" className="h-8 w-24 shrink-0" aria-hidden="true">
    <path d={d} fill="none" stroke="#C26D38" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const BeamCard = ({ b, i, t }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className={`group flex flex-col ${b.wide ? "lg:col-span-2" : ""}`}
    data-testid={`custom-beam-${b.id}`}
  >
    <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#121110]/10 lg:aspect-auto lg:h-[380px]">
      <Pic src={b.img} alt={t(`cb.${b.id}`, b.shape)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <span className="absolute left-4 top-4 rounded-full bg-[#F7F5F0] px-3 py-1 font-mono text-xs tracking-[0.2em] text-[#121110]">{String(i + 1).padStart(2, "0")}</span>
    </div>
    <div className="mt-5 flex items-start justify-between gap-4 border-t border-[#121110]/20 pt-5">
      <div>
        <h3 className="font-display text-2xl font-bold leading-tight tracking-tight transition-transform duration-500 group-hover:translate-x-1">{t(`cb.${b.id}`, b.shape)}</h3>
        <p className="mt-2 text-sm text-[#121110]/75">{t(`proj.${b.project}`, titleOf(b.project))}</p>
      </div>
      <Profile d={b.path} />
    </div>
    <QuoteSimilarButton p={projectOf(b.project)} t={t} className="mt-6 self-start" testId={`custom-beam-quote-${b.id}`} />
  </motion.article>
);

const FACTS = [
  ["len", "44 m", "Travi in lamellare fino a 44 metri di lunghezza"],
  ["ce", "CE", "Marcatura CE · certificato 0407-CPR-1459"],
  ["yrs", `${new Date().getFullYear() - 1905}`, "Anni di esperienza nel legno, dal 1905"],
];

const SendDrawing = ({ t }) => {
  const input = useRef(null);
  const onPick = (e) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    const message = t("cb.drawMsg", "Vi invio il disegno di una forma che vorrei realizzare in legno: potete farmi un preventivo?");
    window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Lamellare curvo / elementi speciali", message, source: "drawing" } }));
    window.dispatchEvent(new CustomEvent("quote-attach-files", { detail: files }));
    scrollToId("#contatti");
  };
  return (
    <div className="flex flex-col gap-2">
      <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf,.zip" className="hidden" onChange={onPick} data-testid="custom-beams-drawing-input" />
      <button onClick={() => input.current?.click()} className="inline-flex items-center justify-center gap-3 rounded-full bg-[#121110] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F7F5F0] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#2A2622]" data-testid="custom-beams-cta">
        <Upload className="h-4 w-4" /> {t("cb.cta", "Hai una forma in mente? Mandaci il disegno")} <ArrowRight className="h-4 w-4" />
      </button>
      <span className="px-2 text-xs text-[#121110]/70">{t("cb.ctaHint", "Schizzo, foto o file CAD · PDF, JPG, PNG, DWG, DXF · max 3 file da 10 MB")}</span>
    </div>
  );
};

export const CustomBeams = () => {
  const t = useT();
  return (
    <section id="su-misura" className="grain relative bg-[#F7F5F0] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="custom-beams-section">
      <div className="mx-auto max-w-[1500px]">
        <div className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-[#121110]/75">
          <span className="sl-index"><span className="sl-orig">(∿)</span></span><span className="h-px w-10 bg-[#121110]/40" /><span>{t("cb.label", "Lavorazioni su misura")}</span>
        </div>
        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-8 lg:text-[4.6rem]"
            lines={[t("cb.h1", "Produciamo secondo"), <span key="b">{t("cb.h2a", "ogni")} <em className="font-serif font-normal text-[#C26D38]">{t("cb.h2em", "precisa richiesta.")}</em></span>]}
          />
          <Reveal delay={0.2} className="max-w-md text-base leading-relaxed text-[#121110]/80 sm:text-lg lg:col-span-4">
            {t("cb.intro", "Curve a S e a doppia S, onde, centine, cupole: se una forma si può disegnare, nel nostro stabilimento la trasformiamo in una trave. Ecco alcune delle più particolari che abbiamo prodotto.")}
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-16 grid gap-8 bg-[#121110] p-8 text-[#F7F5F0] sm:p-12 lg:grid-cols-12 lg:items-end lg:p-16" data-testid="custom-beams-claim">
          <p className="font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:col-span-8 lg:text-5xl">
            {t("cb.claim1", "Gli austriaci sono maestri nel lavorare il legno.")}{" "}
            <span className="text-[#D98A52]">{t("cb.claim2", "Noi il legno lo modelliamo dal 1905.")}</span>
          </p>
          <p className="text-base leading-relaxed text-[#F7F5F0]/80 lg:col-span-4">
            {t("cb.claimSub", "Stessa precisione e qualità certificata, con la flessibilità di un'azienda di famiglia che progetta e produce ogni trave su misura, anche quando la forma è fuori dagli schemi.")}
          </p>
          <dl className="grid gap-8 border-t border-white/15 pt-8 sm:grid-cols-3 lg:col-span-12">
            {FACTS.map((f, i) => (
              <div key={f[0]} className="border-l-2 border-[#D98A52] pl-5" data-testid={`custom-beams-fact-${i}`}>
                <dt className="font-display text-5xl font-bold tabular-nums tracking-tight sm:text-6xl">{f[1]}</dt>
                <dd className="mt-2 font-mono text-xs uppercase leading-relaxed tracking-[0.18em] text-[#F7F5F0]/80">{t(`cb.f${i}`, f[2])}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
        <div className="mt-16 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-testid="custom-beams-grid">
          {BEAMS.map((b, i) => <BeamCard key={b.id} b={b} i={i} t={t} />)}
        </div>
        <Reveal delay={0.1} className="mt-20 flex flex-col gap-4 sm:flex-row sm:items-start">
          <SendDrawing t={t} />
          <button onClick={() => scrollToId("#progetti")} className="inline-flex items-center justify-center gap-2 px-4 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#121110] underline-offset-4 transition-colors duration-300 hover:text-[#C26D38] hover:underline" data-testid="custom-beams-projects-link">
            {t("cb.all", "Vedi tutti i progetti")} <ArrowUpRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
